/**
 * O armazém visto pelo React.
 *
 * `useSyncExternalStore` é o caminho certo aqui: o armazém já é a fonte da
 * verdade e já avisa quem escuta, então copiá-lo para dentro de um `useState`
 * só criaria uma segunda verdade para desencontrar da primeira.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { Documento } from '../types/dados';
import { ligacao } from './ligacao';

export function usarColecao<T = Record<string, unknown>>(
  nome: string,
): ReadonlyArray<Documento<T>> {
  const { armazem, aoVivo } = ligacao();
  const inscrever = useCallback((f: () => void) => armazem.inscrever(nome, f), [armazem, nome]);
  const ler = useCallback(() => armazem.colecao<T>(nome), [armazem, nome]);

  // Sem fluxo (React Native), ninguém enche o armazém sozinho: a tela que pede
  // a coleção é quem a busca. Com fluxo isto não roda, e o `ligar()` do
  // armazém continua sendo a única fonte.
  useEffect(() => {
    if (aoVivo) return;
    void armazem.carregar(nome).catch(() => {
      // O erro já vira `armazem.erro`; a tela mostra por lá.
    });
  }, [armazem, aoVivo, nome]);

  return useSyncExternalStore(inscrever, ler, ler);
}

/**
 * A coleção já ordenada e filtrada, sem refazer o array a cada render.
 *
 * A dependência é a referência da coleção, que o armazém só troca quando algo
 * muda de verdade; por isso ordenar aqui não custa a cada quadro.
 */
export function usarColecaoOrdenada<T = Record<string, unknown>>(
  nome: string,
  comparar?: (a: Documento<T>, b: Documento<T>) => number,
): ReadonlyArray<Documento<T>> {
  const itens = usarColecao<T>(nome);
  return useMemo(() => (comparar ? [...itens].sort(comparar) : itens), [itens, comparar]);
}

/**
 * Um documento só, para as coleções que têm um documento por chave: o cardápio
 * e a lista de compras da semana, a despensa da casa.
 *
 * Escuta a coleção inteira porque é assim que o armazém avisa, mas devolve só
 * a gaveta pedida — que é sempre a mesma referência enquanto ninguém a mudar,
 * como o `useSyncExternalStore` exige.
 */
export function usarDocumento<T = Record<string, unknown>>(
  nome: string,
  id: string,
): T | null {
  const { armazem, aoVivo } = ligacao();
  const inscrever = useCallback((f: () => void) => armazem.inscrever(nome, f), [armazem, nome]);
  const ler = useCallback(() => armazem.documento<T>(nome, id), [armazem, nome, id]);

  useEffect(() => {
    if (aoVivo) return;
    void armazem.carregar(nome).catch(() => {
      // O erro já vira `armazem.erro`; a tela mostra por lá.
    });
  }, [armazem, aoVivo, nome]);

  return useSyncExternalStore(inscrever, ler, ler);
}

export interface EstadoDoArmazem {
  pronto: boolean;
  erro: string | null;
}

/**
 * `pronto` separa "ainda chegando" de "vazio mesmo". Sem essa diferença toda
 * tela vazia mente por um instante, e uma lista de lembranças vazia dói.
 */
export function usarEstadoDoArmazem(): EstadoDoArmazem {
  const { armazem } = ligacao();
  const inscrever = useCallback((f: () => void) => armazem.inscreverEstado(f), [armazem]);
  // O objeto tem que ser estável entre avisos, senão o React re-renderiza sem
  // parar; por isso a leitura devolve os dois campos crus e o `useMemo` monta.
  const pronto = useSyncExternalStore(
    inscrever,
    () => armazem.pronto,
    () => armazem.pronto,
  );
  const erro = useSyncExternalStore(
    inscrever,
    () => armazem.erro,
    () => armazem.erro,
  );
  return useMemo(() => ({ pronto, erro }), [pronto, erro]);
}
